import { parseEntryTime, parseEntryTimeInMinutes, getEntryTimestamp, formatDateKey } from '../../../utils/dateTimeUtils';
angular.module('leanlaw').directive('dayBlock', ['$state', 'userService', function ($state, userService) {
  return {
    restrict: 'E',
    scope: {
      day: '=',
      resolution: '=',
      extraWidth: '='
    },
    template: require('./views/dayBlock.html'),
    link($scope, element, attrs, ctrl) {
      $scope.hours = [...Array(25).keys()];

      // Preselect the day header for better scroll performance
      const dayHeader = element[0].querySelector('.day-header');
      const hoverIndicator = element[0].querySelector('.hover-indicator');
      const hoverGhost = element[0].querySelector('.hover-ghost');
      const hoverGhostBar = hoverGhost.querySelector('.horizontal-bar');
      const entryWidth = 250;

      // The minimum drag is just short of the height of one entry
      const minDragDistance = $scope.resolution - 4;

      // These are also in timeplanner.ctrl
      const entryHeaderHeight = 59; // This is the header on each dayBlock
      const planner = document.getElementById('planner-content');
      let plannerRect = planner.getBoundingClientRect();

      // Used for dragging and creating an entry
      $scope.isDragging = false;
      $scope.isCreatingEntry = false;
      $scope.startingTimeString = '--:--';
      $scope.endingTimeString = '--:--';
      $scope.billableHours = 0;
      $scope.nonBillableHours = 0;
      $scope.fixedFeeHours = 0;
      $scope.storedEntriesList = [];

      // Colors for each entry
      const colors = {
        default: {
          light: '#29bf12',
          dark: '#37846e'
        },
        nonBillable: {
          light: '#e8e8e8',
          dark: '#cecece'
        }
      };
      let defaultHour;
      let useNewTimeEntryModal = false;
      let useDateHandling = false;
      let userServiceLoaded = false;
      userService.me().then(me => {
        defaultHour = me.company.settings.timeEntryIncrement || 0.1;
        useNewTimeEntryModal = (me.features?.timeEntriesTab ?? false) && (me.settings?.enableNewTimeEntry ?? false);
        useDateHandling = me.features?.dateHandling ?? false;
        userServiceLoaded = true;

        // Entries are now loaded by parent controller via batch fetch
        // Check if parent already has entries loaded for this day
        if (!$scope.entriesLoaded) {
          loadEntriesFromParent();
        }
      });

      // Returns true if this block represents today
      $scope.getIsToday = function () {
        const compare = $scope.day;
        const today = new Date();
        if (today.getFullYear() !== compare.getFullYear()) return false;
        if (today.getMonth() !== compare.getMonth()) return false;
        if (today.getDate() !== compare.getDate()) return false;
        return true;
      };
      $scope.getIsFuture = function () {
        const compare = $scope.day;
        const today = new Date();
        return compare.getTime() > today.getTime();
      };

      // Returns the name of the weekday
      $scope.getDayName = function () {
        const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        return days[$scope.day.getDay()];
      };
      $scope.getMonthName = function () {
        const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
        return months[$scope.day.getMonth()].toUpperCase();
      };

      // Simple linear interpolation for color generation
      // Takes hex colors and produces hex output (including "#")
      // https://gist.github.com/rosszurowski/67f04465c424a9bc0dae
      const lerp = function lerp(a, b, amount) {
        // Cap amount to [0, 1]
        amount = Math.min(1, amount);
        amount = Math.max(0, amount);
        const ah = parseInt(a.replace(/#/g, ''), 16);
        const ar = ah >> 16;
        const ag = ah >> 8 & 0xff;
        const ab = ah & 0xff;
        const bh = parseInt(b.replace(/#/g, ''), 16);
        const br = bh >> 16;
        const bg = bh >> 8 & 0xff;
        const bb = bh & 0xff;
        const rr = ar + amount * (br - ar);
        const rg = ag + amount * (bg - ag);
        const rb = ab + amount * (bb - ab);
        return `#${((1 << 24) + (rr << 16) + (rg << 8) + rb | 0).toString(16).slice(1)}`;
      };
      const editEntry = function (entry) {
        // Use new Vue modal if feature flag is enabled, otherwise use old modal
        if (useNewTimeEntryModal) {
          $state.go('timeplanner.edit2', {
            entryId: entry.id
          });
        } else {
          $state.go('timeplanner.edit', {
            entryId: entry.id
          });
        }
      };

      // Adds an entry element and returns element reference
      const addEntry = function addEntry(entry) {
        const entryElem = document.createElement('div');
        entryElem.className += ' entry';
        element[0].appendChild(entryElem);
        const entryTitle = document.createElement('div');
        entryTitle.className += ' title';
        entryTitle.textContent = entry.matter.client.label;
        entryElem.appendChild(entryTitle);
        const entrySubtitle = document.createElement('div');
        entrySubtitle.className += ' subtitle';
        entrySubtitle.textContent = entry.matter.name;
        entryElem.appendChild(entrySubtitle);
        const entryDescription = document.createElement('div');
        entryDescription.className += ' description';
        entryDescription.textContent = entry.description;
        entryElem.appendChild(entryDescription);
        const entryTimespan = document.createElement('div');
        entryTimespan.className += ' timespan';
        entryTimespan.textContent = entry.timespan || ''; // Edge renders undefined as string
        entryElem.appendChild(entryTimespan);
        const entryHours = document.createElement('div');
        entryHours.className += ' hours';
        entryHours.textContent = `${entry.hours}h`;
        entryElem.appendChild(entryHours);

        // Handles click events on individual entry
        // NOTE! Immediate function declaration and execution
        entryElem.onclick = function (event) {
          event.preventDefault();
          event.stopPropagation();
          $scope.$apply(() => {
            editEntry(entry);
          });
        };

        // These are needed to prevent click/drag events on entries
        // from creating new entries.
        entryElem.onmousedown = event => {
          event.preventDefault();
          event.stopPropagation();
        };
        entryElem.onmouseup = event => {
          if (!$scope.isCreatingEntry) {
            event.preventDefault();
            event.stopPropagation();
          }
        };
        return entryElem;
      };

      // Clears previously-rendered entries from the day block
      const clearEntries = function clearEntries() {
        const entries = element[0].querySelectorAll('.entry');
        for (let i = 0; i < entries.length; i++) {
          entries[i].remove();
        }
      };

      // Places entries in their appropropriate positions
      // Recursive
      const renderEntries = function renderEntries(entryPool, depth) {
        const items = angular.copy(entryPool || buildEntryTree());
        if (!depth) depth = 0;

        // Clear entries when rendering roots
        if (!entryPool) clearEntries();
        for (let i = 0; i < items.length; i++) {
          const entry = items[i];
          const entryElem = addEntry(entry);

          // Calculate equivalent number of hours for render
          // This ensures that the entries render properly when
          // the user zooms in/out
          const scaledHours = entry.hours * ($scope.resolution / $scope.$parent.defaultResolution);

          // Hours were determined based on appearance/scale
          const isTinyEntry = scaledHours <= 0.16;
          const isShortEntry = scaledHours <= 0.5;
          const isMediumEntry = scaledHours <= 0.9;

          // Determine height from number of hours
          let entryHeight = entry.hours * 10 * $scope.resolution;

          // if entry hour is less than .03 then set height to 3 for visibility
          if (entryHeight < 3) entryHeight = 3;

          // Add small border between entries
          entryHeight -= 1;

          // Determine top position of entry
          let entryPos;
          if (useDateHandling) {
            // NEW: Support localDateTime format (Story #41530) with fallback to Date object
            const timeData = parseEntryTime(entry, true);
            if (!timeData) {
              console.error('Invalid entry time data, skipping entry:', entry);
              continue; // Skip this entry but continue rendering others
            }
            // Hours contribution
            entryPos = timeData.hours * $scope.resolution * 10;
            // Minutes contribution
            entryPos += timeData.minutes / 6 * $scope.resolution;
          } else {
            // OLD: Legacy Date object methods (backward compatible)
            entryPos = entry.startTime.getHours() * $scope.resolution * 10;
            entryPos += entry.startTime.getMinutes() / 6 * $scope.resolution;
          }
          let maxDepth = 0;
          if (entry.hasOwnProperty('children')) maxDepth = getMaxDepth([entry]);

          // Separation between each entry in the entry stack
          // This is the horizontal distance between each card
          const cardSpacing = 10;
          let right = 15;
          right += cardSpacing * maxDepth; // 0 on deep leaf
          let left = 15;
          left += cardSpacing * depth;

          // Select color palette
          let selectedColor = colors.default;
          let textColor = '#fff';
          if (entry.billingType !== 'billable') {
            selectedColor = colors.nonBillable;
            textColor = '#000';
          }

          // Determine color of this entry depending on depth
          const color = lerp(selectedColor.light, selectedColor.dark, depth / (maxDepth + 3));

          // Apply
          entryElem.style.height = `${entryHeight}px`;
          entryElem.style.top = `${entryPos}px`;
          entryElem.style.left = `${left}px`;
          entryElem.style.right = `${right}px`;
          entryElem.style.backgroundColor = color;
          entryElem.style.color = textColor;
          entryElem.style.zIndex = 10 + maxDepth;

          // Simplified rendering for short entries
          if (isShortEntry) {
            entryElem.className += ' short';
            entryElem.style.lineHeight = `${entryHeight}px`;
            if (isTinyEntry) {
              entryElem.className += ' tiny';
            }
          } else if (isMediumEntry) {
            entryElem.className += ' medium';
          }

          // Render children recursively
          if (entry.hasOwnProperty('children') && !!entry.children.length) {
            renderEntries(entry.children, depth + 1);
          }
        }

        // Make sure bounding box is up to date
        plannerRect = planner.getBoundingClientRect();
      };

      // Load entries from parent's batch-fetched data (fixes N+1 API calls issue)
      const loadEntriesFromParent = function loadEntriesFromParent() {
        const dayKey = formatDateKey($scope.day);
        const parentEntriesData = $scope.$parent.entriesByDay;
        if (parentEntriesData && parentEntriesData[dayKey]) {
          const dayData = parentEntriesData[dayKey];
          // Store copy (because the object is immediately destroyed)
          $scope.storedEntriesList = angular.copy(dayData.entries);
          $scope.billableHours = dayData.summary.billableHours;
          $scope.nonBillableHours = dayData.summary.nonBillableHours;
          $scope.fixedFeeHours = dayData.summary.fixedFeeHours;
          $scope.entriesLoaded = true;
          renderEntries();
        } else {
          // Parent data not ready yet, will be populated via entriesLoaded broadcast
          $scope.storedEntriesList = [];
          $scope.billableHours = 0;
          $scope.nonBillableHours = 0;
          $scope.fixedFeeHours = 0;
          renderEntries();
        }
      };

      // Listen for parent's batch entries loaded event
      $scope.$on('entriesLoaded', function () {
        loadEntriesFromParent();
      });

      // Determines if prospective entry is contained within parent
      // (i.e., if the beginning is contained in parent)
      const entryContains = function entryContains(parent, prospect) {
        let start, compareStart;
        if (useDateHandling) {
          // NEW: Support localDateTime format (Story #41530)
          const parentStartMin = parseEntryTimeInMinutes(parent, true);
          start = parentStartMin * 60;
          const prospectStartMin = parseEntryTimeInMinutes(prospect, true);
          compareStart = prospectStartMin * 60;
        } else {
          // OLD: Legacy Date object methods (backward compatible)
          start = parent.startTime.getHours() * 3600;
          start += parent.startTime.getMinutes() * 60;
          compareStart = prospect.startTime.getHours() * 3600;
          compareStart += prospect.startTime.getMinutes() * 60;
        }
        const end = start + parent.hours * 3600;
        return compareStart >= start && compareStart < end;
      };

      // Places the element as deep as logically possible in parent
      // entry according to the startTime of child
      const deepPlaceEntry = function deepPlaceEntry(parent, child) {
        // Check if parent has children array
        if (!parent.hasOwnProperty('children')) {
          parent.children = [];
          parent.children.push(child);
          return;
        }

        // Assume this child will be a sibling
        let isSibling = true;
        for (let i = 0; i < parent.children.length; i++) {
          const node = parent.children[i];

          // Recurse into containing children
          if (entryContains(node, child)) {
            deepPlaceEntry(node, child);
            isSibling = false;
            break;
          }
        }

        // Add as sibling
        if (isSibling) parent.children.push(child);
      };

      // Returns maximum depth of array of entries (pool)
      const getMaxDepth = function getMaxDepth(pool, iteration) {
        if (typeof iteration === 'undefined') iteration = 0;
        pool = angular.copy(pool || []);
        let maxDepth = iteration;
        for (let i = 0; i < pool.length; i++) {
          if (pool[i].hasOwnProperty('children')) {
            const depth = getMaxDepth(pool[i].children, iteration + 1);
            maxDepth = Math.max(maxDepth, depth);
          }
        }
        return maxDepth;
      };

      // Returns an entry from pool (depth-first recursive)
      const findEntry = function findEntry(pool, entryId) {
        for (let i = 0; i < pool.length; i++) {
          // Compare IDs
          if (pool[i].id == entryId) return pool[i];
          if (pool[i].hasOwnProperty('children')) {
            // Recursively search tree
            const needle = findEntry(pool[i].children, entryId);
            if (needle) return needle;
          }
        }
        return null;
      };

      // Returns array containing nested time entries
      const buildEntryTree = function buildEntryTree() {
        const items = angular.copy($scope.storedEntriesList) || [];

        // Sort items by entry time
        if (useDateHandling) {
          // NEW: Support localDateTime format (Story #41530)
          items.sort((a, b) => {
            const timeA = getEntryTimestamp(a, true) || 0;
            const timeB = getEntryTimestamp(b, true) || 0;
            return timeA - timeB;
          });
        } else {
          // OLD: Legacy Date object sorting (backward compatible)
          items.sort((a, b) => a.startTime.getTime() - b.startTime.getTime());
        }

        // Array of root nodes
        const output = [];

        // Array of entries which have been placed
        const placedEntries = [];

        // Iterate over available items
        for (let i = 0; i < items.length; i++) {
          // Assume this entry will be a root node
          let isRootNode = true;
          const wasPlaced = false;

          // Iterate over placed root nodes
          for (let j = 0; j < output.length; j++) {
            const placedEntry = output[j];
            if (entryContains(placedEntry, items[i])) {
              // Place entry in parent
              deepPlaceEntry(placedEntry, items[i]);
              placedEntries.push(items[i]);
              isRootNode = false;
              break;
            }
          }
          if (!isRootNode) continue;
          output.push(items[i]);
          placedEntries.push(items[i]);
        }
        return output;
      };

      // Moves times to their appropriate positions
      const repositionTimeMarks = function () {
        const hourMarks = element[0].querySelectorAll('.hour-mark');
        for (let i = 0; i < hourMarks.length; i++) {
          hourMarks[i].style.top = `${(i - 1) * $scope.resolution * 10}px`;
          hourMarks[i].style.height = `${$scope.resolution * 10}px`;
        }
        renderEntries();
      };

      // Resizes the block and positions the header properly
      const resizeBlock = function (extraWidth) {
        // var bounding = element[0].getBoundingClientRect();
        const {
          left
        } = element[0];
        element[0].style.width = `${extraWidth + entryWidth}px`;
        dayHeader.style.left = `${left}px`;
        dayHeader.style.width = `${element[0].offsetWidth}px`;
        renderEntries();
      };
      repositionTimeMarks();
      resizeBlock($scope.extraWidth);
      // loadEntries() is now called after userService.me() resolves to avoid race condition

      let dragStartY = 0;
      // This is the snapped position of dragStartY (used for interval calculation)
      let actualDragStartY = 0;
      let dragStopY = 0;

      // Calculate relative position of a mouse Y in the planner
      const calculateRelativePosition = function (mouseY) {
        // plannerRect is updated elsewhere because this function
        // is called frequently and the getBoundingClientRect()
        // call forces a reflow/render cycle (slow)
        const relPos = mouseY + planner.scrollTop - plannerRect.top - entryHeaderHeight;

        // Cap to values > 0
        return Math.max(0, relPos);
      };

      // Snaps a value to 0.1 or 0.25 intervals according to resolution
      const snapValueToIntervals = function (position) {
        // resoltion is the height of one 6-miinute interval
        const interval = $scope.resolution * defaultHour * 10;
        const snap = Math.round(position / interval) * interval;
        return snap;
      };

      // Creates a new entry
      const createEntry = function (startTime, hours) {
        const entryData = {
          entry: {
            startTime,
            hours,
            account: $scope.$parent.plannerAccount
          }
        };

        // Use new Vue modal if feature flag is enabled, otherwise use old modal
        if (useNewTimeEntryModal) {
          $state.go('timeplanner.new2', {
            entry: {
              startTime: startTime,
              hours: hours,
              account: $scope.$parent.plannerAccount
            }
          });
        } else {
          $state.go('timeplanner.new', {
            entry: {
              startTime: startTime,
              hours: hours,
              account: $scope.$parent.plannerAccount
            }
          });
        }
      };

      // Returns a rounded duration string for a duration of time
      const getDurationString = function (difference) {
        // Snap the difference to interval
        difference = snapValueToIntervals(difference);

        // The height of an hour in pixels
        // Remember that resolution is the height of one 6-min interval
        const hourHeight = $scope.resolution * 10;

        // Calculate the time string corresponding to this time
        let numHours = difference / hourHeight;

        // Fix rounding errors, round to 2 places
        numHours = Math.round(numHours * 100) / 100;
        return `${numHours}h`;
      };
      const handleMouseMove = function (event) {
        const position = calculateRelativePosition(event.clientY);
        const snappedPosition = snapValueToIntervals(position);

        // Determine if min drag distance was met
        if (Math.abs(position - dragStartY) > minDragDistance) {
          const height = Math.abs(position - dragStartY);

          // Update height of top bar
          hoverGhostBar.style.height = `${height}px`;

          // Check if user is dragging upwards
          if (position - dragStartY < 0) {
            // Accounts for the default margin-top:
            hoverGhostBar.style.marginTop = `${-height - 19}px`;
          } else {
            // Default
            hoverGhostBar.style.marginTop = `${-19}px`;
          }
          const durationString = getDurationString(Math.abs(snappedPosition - actualDragStartY));
          hoverIndicator.querySelector('.hover-time span.duration-field').textContent = durationString;

          // Don't cause multiple digest cycles
          if ($scope.isCreatingEntry) return;

          // User is dragging to create an entry
          $scope.$apply(() => {
            $scope.isCreatingEntry = true;
          });
        } else {
          if (!$scope.isCreatingEntry) return;

          // User is no longer dragging to create an entry
          $scope.$apply(() => {
            $scope.isCreatingEntry = false;
          });
        }
      };

      // Converts a time string into a Date object
      // This is a bit strange, but the date string must be the
      // source of truth on the frontend.
      const convertTimeStringToDate = function (timeString) {
        // Split apart string
        let hours = parseInt(timeString.split(':')[0]);
        const minutes = parseInt(timeString.split(':')[1].split(' ')[0]);

        // Convert 12 AM to 00
        if (timeString.indexOf('AM') !== -1 && hours == 12) {
          hours = 0;
        }
        // General 12 -> 24 conversion
        if (timeString.indexOf('PM') !== -1 && hours != 12) {
          hours += 12;
        }
        const day = new Date($scope.day);
        day.setSeconds(0);
        day.setMinutes(minutes);
        day.setHours(hours);
        return day;
      };

      // Event handlers for click and drag
      element.bind('mousedown', event => {
        dragStartY = calculateRelativePosition(event.clientY);
        // Cap to values > 0
        dragStartY = Math.max(dragStartY, 0);
        actualDragStartY = snapValueToIntervals(dragStartY);
        $scope.isDragging = true;
        $scope.isCreatingEntry = false;

        // Position hover ghost at the initial drag location
        hoverGhost.style.top = `${dragStartY + entryHeaderHeight}px`;

        // Store the time string for the start
        $scope.startingTimeString = hoverIndicator.querySelector('.hover-time span.time-field').textContent;

        // Start watching mouse move events
        element.bind('mousemove', handleMouseMove);
        $scope.$digest();
      });
      element.bind('mouseup', event => {
        dragStopY = calculateRelativePosition(event.clientY);
        $scope.isDragging = false;
        $scope.isCreatingEntry = false;

        // Stop watching mouse move events
        element.unbind('mousemove', handleMouseMove);

        // Store the time string for the end
        $scope.endingTimeString = hoverIndicator.querySelector('.hover-time span.time-field').textContent;

        // Clear the duration string to prevent flicker
        hoverIndicator.querySelector('.hover-time span.duration-field').textContent = '';

        // Determine the earliest date in the click/drag
        // Since the user can drag up or down, it doesn't really
        // matter what we call the start/end times

        // Use the date reported to the user as the source of truth here,
        // since the actual pixel values can be fuzzy
        const startDate = convertTimeStringToDate($scope.startingTimeString);
        const endDate = convertTimeStringToDate($scope.endingTimeString);

        // Determine which is earliest/latest
        const minDate = Math.min(startDate.getTime(), endDate.getTime());
        const startTime = new Date(minDate);

        // Calculate duration
        const durationMs = Math.abs(startDate.getTime() - endDate.getTime());
        let durationHours = durationMs / 1000 / 60 / 60;
        // Round out (to two places) any rounding errors
        durationHours = Math.round(durationHours * 100) / 100;
        $scope.$digest();

        // Ignore cases where the user has moved their cursor out
        // of the dayBlock. This creates an invalid date.
        if (isNaN(startTime.getTime())) return;

        // Show modal
        createEntry(startTime, durationHours || defaultHour);
      });

      // Clear selection when mouse leaves the row
      // This isn't the best UX but it is buggy otherwise
      const handleMouseLeave = function () {
        $scope.isDragging = false;
        $scope.isCreatingEntry = false;
        element.unbind('mousemove', handleMouseMove);
      };
      element.bind('mouseleave', event => {
        handleMouseLeave();
        $scope.$digest();
      });

      // Update views
      $scope.$watch('entries.length', (newValue, oldValue) => {
        // if (newValue == oldValue) return;
        renderEntries();
      });
      $scope.$watch('resolution', (newValue, oldValue) => {
        // if (newValue == oldValue) return;
        repositionTimeMarks();
        renderEntries();
      });
      // Resize when the extra width
      $scope.$watch('extraWidth', (newValue, oldValue) => {
        resizeBlock(newValue);
      });
    }
  };
}]);